import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, CircularProgress } from "@mui/material";

interface DuplicateReviewBannerProps {
  loading: boolean;
  hasDuplicates: boolean;
  reviewed: boolean;
  onReview: () => void;
}

/**
 * Summary-step banner for the duplicate-expense check: a loading state while
 * the check runs, then a warning with a Review CTA when potential duplicates
 * are found. Renders nothing when there are none.
 */
const DuplicateReviewBanner: React.FC<DuplicateReviewBannerProps> = ({
  loading,
  hasDuplicates,
  reviewed,
  onReview,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Alert
        key="checking"
        severity="info"
        role="status"
        variant="outlined"
        icon={<CircularProgress size={18} />}
      >
        {t("duplicateCheckLoading")}
      </Alert>
    );
  }

  if (!hasDuplicates) return null;

  return (
    // Keyed apart from the loading notice so it mounts fresh and is announced.
    <Alert
      key="found"
      severity="warning"
      role="alert"
      variant="standard"
      sx={{
        alignItems: "center",
        "& .MuiAlert-action": { alignItems: "center", py: 0, mr: 0 },
      }}
      action={
        <Button size="small" variant="primary-filled" onClick={onReview}>
          {reviewed ? t("Reviewed") : t("Review")}
        </Button>
      }
    >
      {t("duplicateReimbursementWarning")}
    </Alert>
  );
};

export default DuplicateReviewBanner;
